import {
  DOMAIN_FIELD_NAME,
  DOMAIN_RESOLVER_NAME,
} from 'components/Dashboard/shared/constants';
import { DynamicFieldType } from '../DynamicFormField';
import { SelectedValuesType } from '../DynamicFieldContext';
import { ControllerProps } from 'react-hook-form';
import { TFunction } from 'i18next';

export const DROPDOWN_MAX_HEIGHT = 300;

export const handleToggle = (
  currentValues: string[],
  value: string,
  singleSelect: boolean,
): string[] => {
  if (singleSelect) {
    return [value];
  }

  return currentValues.includes(value)
    ? currentValues.filter((v) => v !== value)
    : [...currentValues, value];
};

export const handleRemoveChip = (currentValues: any, value: string): string[] => {
  // Ensure currentValues is an array
  if (!Array.isArray(currentValues)) {
    return [];
  }

  // Filter out the specified value if currentValues is a valid array
  return currentValues.filter((v) => v !== value);
};

/**
 * CMSADMIN-2045 - domain/hostname field special ux case.
 * Checks the field configuration matches domain field
 * by name and resolver. Also specify on the dynamic
 * field fieldOptions={{ denumView: 'domain' }} to display
 * the custom domain ui for this type in DynamicFormField.
 */
export const getIsDomainField = (fieldConfig: DynamicFieldType): boolean => {
  return (
    fieldConfig.name === DOMAIN_FIELD_NAME &&
    fieldConfig.config.resolver === DOMAIN_RESOLVER_NAME
  );
};

export const getTooltipText = (fieldConfig: DynamicFieldType): string | null => {
  return fieldConfig.tooltip || fieldConfig.help;
};

/**
 * Transform DynamicFieldType to react-hook-form validation rules.
 * @param field DynamicFieldType object representing the field configuration.
 * @param t i18next translation function.
 * @returns react-hook-form validation rules.
 */
export const getValidationRules = ({
  field,
  t,
}: {
  field: DynamicFieldType;
  t: TFunction;
}): ControllerProps['rules'] => {
  const rules: ControllerProps['rules'] = {};

  // Is required validation.
  if (field.required) {
    rules.required =
      t('blox.error.required', { field: field.label || field.name }) ||
      `${field.label || field.name} is required`;
  }

  // Number.
  if (field.type === 'number' && field.config) {
    if (field.config.min !== undefined) {
      rules.min = {
        value: field.config.min as number,
        message:
          t('blox.error.min', { field: field.label, min: field.config.min }) ||
          `Minimum value is ${field.config.min}`,
      };
    }

    if (field.config.max !== undefined) {
      rules.max = {
        value: field.config.max as number,
        message:
          t('blox.error.max', { field: field.label, max: field.config.max }) ||
          `Maximum value is ${field.config.max}`,
      };
    }
  }

  return rules;
};

/**
 * Normalizes SelectedValuesType into value string[], removing empty strings.
 *  - string[]                   plain string IDs (common)
 *  - { items: string[], ... }   wrapped array shape (dtree)
 *  - string                     single value, wrapped into an array
 */
export function normalizeSelectedValues(selectedValues: SelectedValuesType): string[] {
  if (Array.isArray(selectedValues)) {
    return selectedValues.filter(
      (value) => typeof value === 'string' && value.trim() !== '',
    );
  }

  // dtree items handling: { items: string[], ... }
  if (selectedValues && typeof selectedValues === 'object' && 'items' in selectedValues) {
    return selectedValues.items?.filter(
      (value) => typeof value === 'string' && value.trim() !== '',
    );
  }

  // Plain string — wrap in a single-item array
  if (typeof selectedValues === 'string') {
    return selectedValues.trim() !== '' ? [selectedValues] : [];
  }

  return [];
}

/**
 * Clean label for mui outlined display.
 * Some fields label is " " (empty space) due
 * to legacy reasons and this causes MUI outlined
 * to have a space gap where the label should be.
 */
export function getFieldLabel(fieldConfig: DynamicFieldType): string {
  const fieldLabel = fieldConfig?.label ?? '';
  const isString = typeof fieldConfig?.label === 'string';
  const fmtLabel = isString ? fieldLabel.trim() : String(fieldLabel);
  return fmtLabel;
}

/**
 * True if the field config string is denum
 */
export function isDenumType(fieldType?: string): boolean {
  if (!fieldType || typeof fieldType !== 'string') {
    return false;
  }
  const nFieldType = fieldType?.toLowerCase() || '';
  return nFieldType === 'denum';
}

/**
 * True if the field config string is enumeration
 */
export function isEnumType(fieldType?: string): boolean {
  if (!fieldType || typeof fieldType !== 'string') {
    return false;
  }
  const nFieldType = fieldType?.toLowerCase() || '';
  return nFieldType === 'enumeration';
}

/**
 * True if the field config string is group
 */
export function isGroupType(fieldType?: string): boolean {
  if (!fieldType || typeof fieldType !== 'string') {
    return false;
  }
  const nFieldType = fieldType?.toLowerCase() || '';
  return nFieldType === 'group';
}
